import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Avatar,
  Typography,
  Box,
  Chip,
  IconButton
} from '@mui/material';
import {
  Phone,
  Email,
  LocationOn,
  Message
} from '@mui/icons-material';
import { User } from '../../types';
import { useAppSelector } from '../../hooks/redux';
import { ProfileModal } from '../profile/ProfileModal';
import authService from '../../services/auth';
import { getAvatarColors } from '../../utils/avatarColors';

interface FamilyDirectoryProps {
  members: User[];
  onContactMember: (member: User, contactType: 'phone' | 'email') => void;
  onViewLocation: (member: User) => void;
  onEditProfile?: (member: User) => void;
  onOpenChat?: (member: User) => void;
  onMembersUpdate?: (members: User[]) => void;
}

export const FamilyDirectory: React.FC<FamilyDirectoryProps> = ({
  members,
  onContactMember,
  onViewLocation,
  onEditProfile,
  onOpenChat,
  onMembersUpdate
}) => {
  const { loginUser: user } = useAppSelector((state) => state.user);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMembers, setFilteredMembers] = useState<User[]>(members);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailedUser, setDetailedUser] = useState<User | null>(null);

  useEffect(() => {
    const filtered = members.filter(member => {
      const name = member.displayName || member.display_name || `${member.firstName || member.first_name || ''} ${member.lastName || member.last_name || ''}`.trim();
      return (name && name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.occupation && member.occupation.toLowerCase().includes(searchTerm.toLowerCase()));
    });
    setFilteredMembers(filtered);
  }, [members, searchTerm]);

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const getLocationText = (member: User) => {
    if (member.address && (member.address.city || member.address.state)) {
      return `${member.address.city || ''}${member.address.city && member.address.state ? ', ' : ''}${member.address.state || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '');
    }
    if (member.location && (member.isLocationVisible || member.is_location_visible)) {
      return 'Live location available';
    }
    return 'No location shared';
  };

  const handleCardClick = async (member: User) => {
    try {
      const profileData = await authService.getUserProfile(member.id);
      setDetailedUser(profileData);
      setSelectedUser(member);
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      setDetailedUser(member);
      setSelectedUser(member);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedUser(null);
    setDetailedUser(null);
  };

  const handleUserUpdate = (updatedUser: User) => {
    setDetailedUser(updatedUser);

    // Also update the member in the members array to reflect changes immediately
    const updatedMembers = members.map(member =>
      member.id === updatedUser.id ? updatedUser : member
    );
    // Force re-render by creating new array reference
    if (onMembersUpdate) {
      onMembersUpdate(updatedMembers);
    }
  };

  const handleEditProfile = (userToEdit: User) => {
    handleCloseModal();
    if (onEditProfile) {
      onEditProfile(userToEdit);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h2" gutterBottom sx={{ fontSize: '2.5em', margin: 0 }}>
          Family Directory
        </Typography>
        <Chip
          label={`${filteredMembers.length} members`}
          variant="outlined"
          sx={{
            borderColor: '#ff9800',
            color: '#ff9800',
            fontWeight: 500
          }}
        />
      </Box>

      <div className="textfield textfield-with-start-adornment" style={{ marginBottom: '24px' }}>
        <div className="textfield-root">
          <div className="input-adornment input-adornment-start">
            <span className="material-icons">search</span>
          </div>
          <input
            className="textfield-input"
            type="text"
            placeholder="Search family members..."
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <Grid container spacing={3}>
        {filteredMembers.map((member) => (
          <Grid item xs={12} sm={6} md={4} key={member.id}>
            <Card
              elevation={0}
              sx={{
                height: '333px',
                cursor: 'pointer',
                backgroundColor: 'var(--background-paper)',
                boxShadow: 'none',
                '&:hover, &:active, &:focus': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
                },
                transition: 'all 0.2s ease-in-out',
                '@media (max-width: 768px)': {
                  maxWidth: '300px',
                  '& .MuiCardContent-root': {
                    padding: '12px'
                  }
                }
              }}
              className="glassContainer fdcard"
              onClick={() => handleCardClick(member)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, minWidth: 0 }}>
                  {(() => {
                    const name = member.displayName || member.display_name || member.firstName || member.first_name || '';
                    const avatarColors = getAvatarColors(name, member.avatar_color);
                    return (
                      <Box sx={{ position: 'relative', width: 96, height: 96, mr: 2, flexShrink: 0 }}>
                        <Avatar
                          src={member.photoURL || member.photo_url}
                          sx={{
                            width: 96,
                            height: 96,
                            position: 'absolute',
                            backgroundColor: (member.photoURL || member.photo_url) ? 'transparent' : avatarColors.backgroundColor,
                            color: avatarColors.color,
                            fontSize: '2.5rem',
                            fontWeight: 'bold',
                            clipPath: 'circle(50%)',
                            '& img': {
                              clipPath: 'circle(50%)'
                            },
                            '@media (max-width: 600px)': {
                              width: 64,
                              height: 64,
                              fontSize: '1.8rem'
                            }
                          }}
                        >
                          {name ? name.charAt(0).toUpperCase() : '?'}
                        </Avatar>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: 96,
                            height: 96,
                            borderRadius: '50%',
                            boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3), inset 0 -1px 2px rgba(255, 255, 255, 0.1)',
                            pointerEvents: 'none',
                            zIndex: 1,
                            '@media (max-width: 600px)': {
                              width: 64,
                              height: 64
                            }
                          }}
                        />
                      </Box>
                    );
                  })()}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="h6" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {member.displayName || member.display_name || `${member.firstName || member.first_name || ''} ${member.lastName || member.last_name || ''}`.trim() || 'Unknown User'}
                      {member.id === user?.id && (
                        <Chip className="you-chip" label="You" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    {member.occupation && (
                      <Typography variant="body2" color="textSecondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {member.occupation}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {member.bio && (
                  <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                    "{member.bio}"
                  </Typography>
                )}

                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                      maxWidth: '100%'
                    }}
                  >
                    <Email sx={{ fontSize: 16, mr: 1, flexShrink: 0, verticalAlign: 'text-bottom' }} />
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        flex: 1
                      }}
                    >
                      {member.email}
                    </Typography>
                  </Box>

                  {(member.phoneNumber || member.phone_number) && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      <Phone sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />
                      {formatPhoneNumber(member.phoneNumber || member.phone_number || '')}
                    </Typography>
                  )}

                  <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                    <LocationOn sx={{ fontSize: 16, mr: 1, verticalAlign: 'text-bottom' }} />
                    {getLocationText(member)}
                  </Typography>

                  {(member.birthday && (member.isBirthdayVisible || member.is_birthday_visible)) && (
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                      <span className="material-icons" style={{ fontSize: 16, marginRight: 8, verticalAlign: 'text-bottom' }}>cake</span>
                      {(() => {
                        try {
                          if (typeof member.birthday === 'string') {
                            // Handle ISO format or YYYY-MM-DD format
                            let dateStr = member.birthday;
                            if (dateStr.includes('T')) {
                              dateStr = dateStr.split('T')[0];
                            }
                            const [year, month, day] = dateStr.split('-');
                            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                            return date.toLocaleDateString('en-US', {
                              month: 'long',
                              day: 'numeric',
                              year: 'numeric'
                            });
                          }
                          return 'Birthday available';
                        } catch (error) {
                          return 'Birthday available';
                        }
                      })()}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onOpenChat) onOpenChat(member);
                    }}
                    disabled={!onOpenChat || member.id === user?.id}
                    color="primary"
                  >
                    <Message />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContactMember(member, 'phone');
                    }}
                    disabled={!(member.phoneNumber || member.phone_number)}
                    color="primary"
                  >
                    <Phone />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContactMember(member, 'email');
                    }}
                    color="primary"
                  >
                    <Email />
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewLocation(member);
                    }}
                    disabled={!(member.isLocationVisible || member.is_location_visible) && !member.address}
                    color="primary"
                  >
                    <LocationOn />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <ProfileModal
        open={modalOpen}
        onClose={handleCloseModal}
        user={detailedUser}
        currentUser={user}
        onEdit={handleEditProfile}
        onContact={onContactMember}
        onViewLocation={onViewLocation}
        onUpdate={handleUserUpdate}
        onOpenChat={onOpenChat}
      />
    </Box>
  );
};